import { ArrowRight, Search, MousePointerClick, Users, Eye, TrendingUp, TrendingDown } from 'lucide-react';
import { cn, formatCompact } from '@/lib/utils';
import { useGSCKPIs } from '@/hooks/useGSCData';
import { useGA4KPIs } from '@/hooks/useGA4Data';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Journey View - Unified funnel visualization
 * Shows the complete journey: Impressions → Clicks → Sessions → Users
 */
export function JourneyView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGA4 = !!activeProperty?.ga4Property;
  const hasGSC = !!activeProperty?.gscSite;

  const { data: gscKpis, isLoading: gscLoading } = useGSCKPIs();
  const { data: ga4Kpis, isLoading: ga4Loading } = useGA4KPIs();

  const isLoading = gscLoading || ga4Loading;

  // Calculate derived metrics
  const clickToSessionRatio = gscKpis?.clicks && ga4Kpis?.sessions
    ? ((ga4Kpis.sessions / gscKpis.clicks) * 100)
    : null;

  const sessionToUserRatio = ga4Kpis?.sessions && ga4Kpis?.users
    ? ((ga4Kpis.users / ga4Kpis.sessions) * 100)
    : null;

  if (!hasGA4 && !hasGSC) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        No property selected
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Search to Site Journey</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Visualize the complete user journey from search impression to engaged user
        </p>
      </div>

      {/* Main Funnel Visualization */}
      <div className="glass-panel p-6">
        <h2 className="text-lg font-medium mb-6">User Journey Funnel</h2>

        <div className="flex items-center justify-between gap-4">
          {/* Stage 1: Impressions */}
          {hasGSC && (
            <>
              <FunnelStage
                icon={Eye}
                label="Impressions"
                value={gscKpis?.impressions ?? 0}
                change={gscKpis?.impressionsChange ?? 0}
                color="emerald"
                isLoading={isLoading}
                description="Times shown in search"
              />
              <ArrowRight className="h-8 w-8 text-muted-foreground/30 flex-shrink-0" />
            </>
          )}

          {/* Stage 2: Clicks */}
          {hasGSC && (
            <>
              <FunnelStage
                icon={MousePointerClick}
                label="Clicks"
                value={gscKpis?.clicks ?? 0}
                change={gscKpis?.clicksChange ?? 0}
                color="emerald"
                isLoading={isLoading}
                description={`${(gscKpis?.ctr ?? 0).toFixed(1)}% CTR`}
              />
              <ArrowRight className="h-8 w-8 text-muted-foreground/30 flex-shrink-0" />
            </>
          )}

          {/* Stage 3: Sessions */}
          {hasGA4 && (
            <>
              <FunnelStage
                icon={Search}
                label="Sessions"
                value={ga4Kpis?.sessions ?? 0}
                change={ga4Kpis?.sessionsChange ?? 0}
                color="blue"
                isLoading={isLoading}
                description="Site visits started"
              />
              <ArrowRight className="h-8 w-8 text-muted-foreground/30 flex-shrink-0" />
            </>
          )}

          {/* Stage 4: Users */}
          {hasGA4 && (
            <FunnelStage
              icon={Users}
              label="Users"
              value={ga4Kpis?.users ?? 0}
              change={ga4Kpis?.usersChange ?? 0}
              color="blue"
              isLoading={isLoading}
              description="Unique visitors"
            />
          )}
        </div>
      </div>

      {/* Conversion Metrics */}
      {hasGSC && hasGA4 && (
        <div className="grid grid-cols-3 gap-4">
          <MetricCard
            title="Search CTR"
            value={`${(gscKpis?.ctr ?? 0).toFixed(1)}%`}
            subtitle="Impressions → Clicks"
            color="emerald"
            isLoading={isLoading}
          />
          <MetricCard
            title="Click Efficiency"
            value={clickToSessionRatio ? `${clickToSessionRatio.toFixed(0)}%` : '-'}
            subtitle="Clicks → Sessions"
            description={clickToSessionRatio && clickToSessionRatio > 100
              ? "Other traffic sources contributing"
              : clickToSessionRatio && clickToSessionRatio < 90
                ? "Check tracking or bot traffic"
                : "Healthy ratio"}
            color="purple"
            isLoading={isLoading}
          />
          <MetricCard
            title="Returning Users"
            value={sessionToUserRatio ? `${(100 - sessionToUserRatio).toFixed(0)}%` : '-'}
            subtitle="Multi-session users"
            color="blue"
            isLoading={isLoading}
          />
        </div>
      )}

      {/* Summary Insights */}
      <div className="glass-panel p-6">
        <h2 className="text-lg font-medium mb-4">Journey Insights</h2>
        <div className="space-y-3">
          {hasGSC && hasGA4 && gscKpis && ga4Kpis && (
            <>
              <InsightRow
                type={gscKpis.ctr > 5 ? 'success' : gscKpis.ctr > 2 ? 'neutral' : 'warning'}
                message={`Your CTR is ${gscKpis.ctr.toFixed(1)}% - ${
                  gscKpis.ctr > 5 ? 'excellent visibility in search!' :
                  gscKpis.ctr > 2 ? 'room for improvement with better titles/descriptions' :
                  'consider optimizing meta titles and descriptions'
                }`}
              />
              <InsightRow
                type={ga4Kpis.bounceRate < 40 ? 'success' : ga4Kpis.bounceRate < 60 ? 'neutral' : 'warning'}
                message={`${ga4Kpis.bounceRate.toFixed(0)}% bounce rate - ${
                  ga4Kpis.bounceRate < 40 ? 'users are engaged with your content' :
                  ga4Kpis.bounceRate < 60 ? 'average engagement, some content may need optimization' :
                  'high bounce rate suggests content mismatch with search intent'
                }`}
              />
              {clickToSessionRatio && clickToSessionRatio > 150 && (
                <InsightRow
                  type="success"
                  message="Strong non-organic traffic! Direct, referral, or social channels are significant."
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface FunnelStageProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  change: number;
  color: 'emerald' | 'blue' | 'purple';
  isLoading: boolean;
  description?: string;
}

function FunnelStage({ icon: Icon, label, value, change, color, isLoading, description }: FunnelStageProps) {
  const colorStyles = {
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    purple: 'bg-purple-500/10 text-purple-400 border-purple-500/30',
  };

  return (
    <div className={cn('flex-1 p-4 rounded-xl border-2 text-center', colorStyles[color])}>
      <div className="flex justify-center mb-2">
        <Icon className="h-8 w-8" />
      </div>
      <p className={cn('text-3xl font-bold', isLoading && 'animate-pulse')}>
        {isLoading ? '-' : formatCompact(value)}
      </p>
      <p className="text-sm font-medium mt-1">{label}</p>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
      {!isLoading && (
        <div className={cn(
          'inline-flex items-center gap-1 mt-2 text-xs font-medium',
          change >= 0 ? 'text-success' : 'text-danger'
        )}>
          {change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
          {Math.abs(change).toFixed(1)}%
        </div>
      )}
    </div>
  );
}

interface MetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  description?: string;
  color: 'emerald' | 'blue' | 'purple';
  isLoading: boolean;
}

function MetricCard({ title, value, subtitle, description, color, isLoading }: MetricCardProps) {
  const borderColors = {
    emerald: 'border-l-emerald-500',
    blue: 'border-l-blue-500',
    purple: 'border-l-purple-500',
  };

  return (
    <div className={cn('glass-panel p-4 border-l-4', borderColors[color])}>
      <p className="text-sm text-muted-foreground">{title}</p>
      <p className={cn('text-2xl font-bold text-foreground mt-1', isLoading && 'animate-pulse')}>
        {isLoading ? '-' : value}
      </p>
      <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
      {description && (
        <p className="text-xs text-muted-foreground/70 mt-2">{description}</p>
      )}
    </div>
  );
}

interface InsightRowProps {
  type: 'success' | 'warning' | 'neutral';
  message: string;
}

function InsightRow({ type, message }: InsightRowProps) {
  const styles = {
    success: 'border-l-success bg-success/5',
    warning: 'border-l-warning bg-warning/5',
    neutral: 'border-l-muted-foreground bg-muted/5',
  };

  return (
    <div className={cn('p-3 rounded-r-lg border-l-4', styles[type])}>
      <p className="text-sm text-foreground">{message}</p>
    </div>
  );
}
